import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, ChevronUp } from "lucide-react";
import { useI18n } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session-scoped dismissal so a closed ad doesn't reappear immediately during the same visit
const DISMISS_KEY = "motravay_sponsor_dismissed";

function useDevice() {
  const [device, setDevice] = useState(
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop"
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setDevice(e.matches ? "mobile" : "desktop");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return device;
}

/**
 * Floating "Sponsored" card. One instance per page mount.
 * - Desktop: fixed bottom-right, ~320px wide
 * - Mobile: fixed bottom, full-width sticky above the browser chrome
 * - Sessionally dismissable per sponsor id (won't reappear until next tab open)
 * - Can be minimized (roll down to a small pill)
 * - Impression logged on mount, click logged on image/button click
 * - Ignores if backend returns no eligible sponsor (master toggle off or no matches)
 */
export default function SponsoredAd({ placement = "all" }) {
  const { lang } = useI18n();
  const device = useDevice();
  const [sponsor, setSponsor] = useState(null);
  const [closed, setClosed] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/sponsors`, { params: { placement, device } });
        if (!mounted) return;
        const s = r.data?.sponsor;
        if (!s) return;
        // Skip if dismissed this session
        try {
          const dismissed = JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]");
          if (dismissed.includes(s.id)) return;
        } catch (_e) { /* ignore */ }
        setSponsor(s);
        // Fire-and-forget impression tracking
        axios.post(`${API}/sponsors/${s.id}/impression`).catch(() => {});
      } catch (_e) { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [placement, device]);

  if (!sponsor || closed) return null;

  const dismiss = () => {
    setClosed(true);
    try {
      const dismissed = JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]");
      if (!dismissed.includes(sponsor.id)) dismissed.push(sponsor.id);
      sessionStorage.setItem(DISMISS_KEY, JSON.stringify(dismissed));
    } catch (_e) { /* ignore */ }
  };

  const track = () => { axios.post(`${API}/sponsors/${sponsor.id}/click`).catch(() => {}); };
  const openLink = () => {
    if (!sponsor.link_url) return;
    track();
    window.open(sponsor.link_url, "_blank", "noopener,noreferrer");
  };

  const sponsoredLabel = lang === "fr" ? "Sponsorisé" : "Sponsored";

  // Minimized pill
  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        data-testid="sponsored-ad-pill"
        className="fixed bottom-4 right-4 md:right-6 z-40 bg-white border border-slate-200 shadow-lg rounded-full px-4 py-2 text-xs font-medium text-slate-700 hover:border-[#1A8FE3] hover:text-[#1A8FE3] flex items-center gap-2"
      >
        <ChevronUp className="h-3 w-3" /> {sponsoredLabel}
      </button>
    );
  }

  // Full card
  const positionCls =
    device === "mobile"
      ? "left-3 right-3 bottom-3"
      : "right-6 bottom-6 w-[320px]";

  return (
    <aside
      role="complementary"
      aria-label={sponsoredLabel}
      data-testid="sponsored-ad"
      data-sponsor-id={sponsor.id}
      className={`fixed z-40 ${positionCls} bg-white border border-slate-200 shadow-2xl rounded-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 bg-slate-50/70">
        <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500">{sponsoredLabel}</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMinimized(true)}
            aria-label="Minimize"
            data-testid="sponsored-ad-minimize"
            className="p-1 rounded hover:bg-slate-200 text-slate-500"
          >
            <ChevronUp className="h-3.5 w-3.5 rotate-180" />
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            data-testid="sponsored-ad-close"
            className="p-1 rounded hover:bg-slate-200 text-slate-500"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Image (clickable) */}
      {sponsor.image_url && (
        <button
          type="button"
          onClick={openLink}
          data-testid="sponsored-ad-image"
          className="block w-full text-left"
        >
          <img
            src={sponsor.image_url.startsWith("http") ? sponsor.image_url : `${process.env.REACT_APP_BACKEND_URL}${sponsor.image_url}`}
            alt={sponsor.name || sponsoredLabel}
            className="w-full h-32 object-cover"
            loading="lazy"
          />
        </button>
      )}

      {/* Body */}
      <div className="p-3">
        <div className="font-display font-semibold text-slate-900 text-sm" data-testid="sponsored-ad-name">{sponsor.name}</div>
        {sponsor.description && (
          <p className="text-xs text-slate-600 mt-1 leading-snug line-clamp-2" data-testid="sponsored-ad-description">{sponsor.description}</p>
        )}
        {sponsor.link_url && (
          <button
            type="button"
            onClick={openLink}
            data-testid="sponsored-ad-cta"
            className="mt-2.5 inline-flex items-center rounded-full bg-[#1A8FE3] hover:bg-[#157AC0] text-white text-xs font-medium px-4 py-1.5 transition-colors"
          >
            {sponsor.button_text || (lang === "fr" ? "Découvrir" : "Learn More")}
          </button>
        )}
      </div>
    </aside>
  );
}
